<template>
  <transition
    appear
    name="p-drawer"
  >
    <div
      v-if="should_show_drawer"
      :class="drawer_classes"
      :style="drawer_inline_style"
      :data-test="data_test"
      role="complementary"
    >
      <!-- Header: client switcher + toggle -->
      <div
        class="flex w-full items-center gap-2"
        :class="is_expanded_effective ? '' : 'flex-col-reverse gap-3'"
      >
        <slot
          name="client_switcher"
          :is_expanded="is_expanded_effective"
        />
        <a
          class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-neutral-600 transition-all duration-150 hover:bg-selected hover:text-neutral-800"
          @click="onToggle"
        >
          <FontAwesomeIcon
            :icon="is_expanded_effective ? 'arrow-left-to-line' : 'arrow-right-to-line'"
            :size="mq.isMobile ? 'lg' : 'sm'"
          />
        </a>
      </div>

      <!-- Nav items -->
      <div class="flex w-full flex-col gap-1">
        <slot
          name="nav"
          :is_expanded="is_expanded_effective"
        />
      </div>

      <!-- Project list (only when expanded) -->
      <div
        v-if="is_expanded_effective && $slots.project_list"
        class="flex min-h-0 flex-1 flex-col"
      >
        <slot
          name="project_list"
          :is_expanded="is_expanded_effective"
        />
      </div>
      <div
        v-else
        class="flex-1"
      />

      <!-- Footer: Ziggu logo (only when expanded) -->
      <div
        v-if="is_expanded_effective"
        class="flex w-full items-center justify-center py-1"
      >
        <img
          :src="ziggu_logo_green"
          alt="Ziggu"
          class="ziggu-logo-subtle h-4"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import useMqService from '@/plugins/mq.js';
import { local_storage } from '@/store/lib/local_storage';
import ziggu_logo_green from '@/assets/images/Ziggu_Logo_Green.png';

const props = defineProps({
  modelValue: { type: Boolean },
  expand_storage_key: { type: String, default: null },
  width_expanded: { type: Number, default: 260 },
  width_collapsed: { type: Number, default: 60 },
  data_test: { type: String, default: null },
});

const emit = defineEmits(['update:modelValue', 'close']);

const mq = useMqService();

const stored_expand = props.expand_storage_key ? local_storage.get(props.expand_storage_key) : {};
const is_expanded = ref(stored_expand.is_expanded === undefined ? true : stored_expand.is_expanded);

// On mobile the drawer is a full-width overlay, so it should always render expanded
// regardless of the persisted desktop collapsed state.
const is_expanded_effective = computed(() => (mq.isMobile ? true : is_expanded.value));

watch(is_expanded, (value) => {
  if (props.expand_storage_key) local_storage.set(props.expand_storage_key, { is_expanded: value });
});

const should_show_drawer = computed(() => (mq.isMobile ? props.modelValue : true));

const drawer_classes = computed(() => {
  const base =
    'group bg-rail border-r border-neutral-200 text-neutral-700 z-40 h-full mb-0 shrink-0 flex flex-col gap-3';
  if (mq.isMobile) {
    return `fixed top-0 left-0 w-full p-2 ${base}`;
  }
  const padding = is_expanded.value ? 'px-4 py-3' : 'px-2 py-3';
  return `relative lg:static top-0 left-0 ${padding} ${base} transition-[width] duration-150 ease-out`;
});

const drawer_inline_style = computed(() => {
  if (mq.isMobile) return {};
  if (!is_expanded.value) return { width: `${props.width_collapsed}px` };
  return { width: `${props.width_expanded}px` };
});

function onToggle() {
  if (mq.isMobile) {
    emit('update:modelValue', false);
    emit('close');
  } else {
    is_expanded.value = !is_expanded.value;
  }
}
</script>

<style scoped>
@media (min-width: 1024px) {
  .lg\:static {
    transition: width 0.15s ease-out;
  }
}

.ziggu-logo-subtle {
  filter: grayscale(100%);
  opacity: 0.45;
}
</style>
